import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { TrackingSettingsCard } from "@/components/TrackingSettingsCard";
import { SecureGate } from "@/components/SecureGate";
import { PrivacyNoticeCard } from "@/components/PrivacyNoticeCard";
import { ReminderSettingsCard } from "@/components/ReminderSettingsCard";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const Card = ({ title, children }) => (
  <section className="rounded-2xl border bg-white p-5 sm:p-6"><h2 className="mb-4 font-display text-lg font-semibold">{title}</h2>{children}</section>
);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;

  const num = (v) => (v === "" ? 0 : Number(v));
  const setSlot = (i, k, v) => setS((p) => ({ ...p, slots: p.slots.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const toggleDay = (d) => setS((p) => ({ ...p, working_days: p.working_days.includes(d) ? p.working_days.filter((x) => x !== d) : [...p.working_days, d].sort() }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Configurazione" title="Orari standard e regole">
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="settings-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva modifiche</Button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Preventivo">
          <p className="text-sm text-slate-500">Prezzi, giorni e orari di ogni tipo si gestiscono in <Link to="/admin/tipi-intervento" className="font-semibold text-[#61a229] hover:underline" data-testid="settings-service-types-link">Tipi di intervento</Link>.</p>
          <div className="mt-5 grid grid-cols-2 gap-3 border-t pt-5">
            <div className="space-y-2"><Label>Frazione di fatturazione (min)</Label><Input type="number" min="1" value={s.billing_fraction_minutes} onChange={(e) => setS((p) => ({ ...p, billing_fraction_minutes: num(e.target.value) }))} data-testid="settings-fraction" /></div>
            <div className="space-y-2"><Label>Ore stimate nel preventivo</Label><Input type="number" min="0" step="0.25" value={s.estimated_hours} onChange={(e) => setS((p) => ({ ...p, estimated_hours: num(e.target.value) }))} data-testid="settings-est-hours" /></div>
          </div>
        </Card>
        <div className="space-y-6">
          <Card title="Fasce orarie standard">
            <p className="mb-3 text-xs text-slate-500">Usate dai tipi di intervento senza orari dedicati.</p>
            <div className="space-y-2">
              {s.slots.map((sl, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`settings-slot-${i}`}>
                  <Input type="time" value={sl.start} onChange={(e) => setSlot(i, "start", e.target.value)} />
                  <span className="text-slate-400">–</span>
                  <Input type="time" value={sl.end} onChange={(e) => setSlot(i, "end", e.target.value)} />
                  <Button variant="ghost" size="icon" onClick={() => setS((p) => ({ ...p, slots: p.slots.filter((_, j) => j !== i) }))} data-testid={`settings-slot-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setS((p) => ({ ...p, slots: [...p.slots, { start: "09:00", end: "10:00" }] }))} data-testid="settings-slot-add"><Plus className="h-4 w-4" /> Aggiungi fascia</Button>
            </div>
            <p className="mt-5 mb-2 text-sm font-semibold">Giorni lavorativi</p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d, i) => (
                <button key={d} type="button" data-active={s.working_days.includes(i)} onClick={() => toggleDay(i)} data-testid={`settings-day-${i}`}
                  className="choice rounded-full border bg-white px-3.5 py-1.5 text-sm">{d}</button>
              ))}
            </div>
          </Card>
          <Card title="Regole">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Preavviso assenze (ore)</Label><Input type="number" min="0" value={s.absence_notice_hours} onChange={(e) => setS((p) => ({ ...p, absence_notice_hours: num(e.target.value) }))} data-testid="settings-notice" /></div>
              <div className="space-y-2"><Label>Preavviso prenotazioni clienti (ore)</Label><Input type="number" min="0" max="720" value={s.booking_notice_hours ?? 0} onChange={(e) => setS((p) => ({ ...p, booking_notice_hours: num(e.target.value) }))} data-testid="settings-booking-notice" />
                <p className="text-xs text-slate-500">Es. 48: chi prenota oggi vede i giorni disponibili solo dal terzo giorno successivo.</p></div>
              <div className="space-y-2"><Label>Giorni prenotabili</Label><Input type="number" min="1" max="90" value={s.booking_days_ahead} onChange={(e) => setS((p) => ({ ...p, booking_days_ahead: num(e.target.value) }))} data-testid="settings-days-ahead" /></div>
              <div className="space-y-2"><Label>Raggio "in zona" (km)</Label><Input type="number" min="1" value={s.near_radius_km} onChange={(e) => setS((p) => ({ ...p, near_radius_km: num(e.target.value) }))} data-testid="settings-radius" /></div>
            </div>
          </Card>
          <ReminderSettingsCard />
          <SecureGate><TrackingSettingsCard /></SecureGate>
          <PrivacyNoticeCard />
        </div>
      </div>
    </div>
  );
}
